import { useRef, useCallback, type KeyboardEvent, type ChangeEvent } from 'react'
import { cn } from '../utils/cn'
import { useTranslations } from '../providers/ContactServiceProvider'

export interface UnifiedPhoneInputProps {
  value: { areaCode: string; number: string; extension: string }
  onChange: (value: { areaCode: string; number: string; extension: string }) => void
  disabled?: boolean
  hasError?: boolean
  id?: string
  'aria-describedby'?: string
}

const AREA_MAX = 3
const NUMBER_MAX = 10
const EXTENSION_MAX = 3

export function UnifiedPhoneInput({
  value,
  onChange,
  disabled = false,
  hasError = false,
  id,
  'aria-describedby': ariaDescribedBy,
}: UnifiedPhoneInputProps) {
  const areaRef = useRef<HTMLInputElement>(null)
  const numberRef = useRef<HTMLInputElement>(null)
  const extensionRef = useRef<HTMLInputElement>(null)
  const t = useTranslations()

  const handleAreaChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.replace(/\D/g, '').slice(0, AREA_MAX)
      onChange({ ...value, areaCode: val })
      if (val.length === AREA_MAX) {
        numberRef.current?.focus()
      }
    },
    [value, onChange],
  )

  const handleNumberChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.replace(/\D/g, '').slice(0, NUMBER_MAX)
      onChange({ ...value, number: val })
      if (val.length === NUMBER_MAX) {
        extensionRef.current?.focus()
      }
    },
    [value, onChange],
  )

  const handleExtensionChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.replace(/\D/g, '').slice(0, EXTENSION_MAX)
      onChange({ ...value, extension: val })
    },
    [value, onChange],
  )

  const handleKeyDown = useCallback(
    (segment: 'area' | 'number' | 'extension') =>
      (e: KeyboardEvent<HTMLInputElement>) => {
        const target = e.currentTarget
        const atStart = target.selectionStart === 0 && target.selectionEnd === 0
        const atEnd =
          target.selectionStart === target.value.length &&
          target.selectionEnd === target.value.length

        if (e.key === 'Backspace' && atStart) {
          e.preventDefault()
          if (segment === 'number') {
            areaRef.current?.focus()
          } else if (segment === 'extension') {
            numberRef.current?.focus()
          }
        }

        if (e.key === 'ArrowLeft' && atStart) {
          e.preventDefault()
          if (segment === 'number' && areaRef.current) {
            areaRef.current.focus()
            const len = areaRef.current.value.length
            areaRef.current.setSelectionRange(len, len)
          } else if (segment === 'extension' && numberRef.current) {
            numberRef.current.focus()
            const len = numberRef.current.value.length
            numberRef.current.setSelectionRange(len, len)
          }
        }

        if (e.key === 'ArrowRight' && atEnd) {
          e.preventDefault()
          if (segment === 'area' && numberRef.current) {
            numberRef.current.focus()
            numberRef.current.setSelectionRange(0, 0)
          } else if (segment === 'number' && extensionRef.current) {
            extensionRef.current.focus()
            extensionRef.current.setSelectionRange(0, 0)
          }
        }
      },
    [],
  )

  if (disabled) {
    const display = [value.areaCode, value.number, value.extension]
      .filter(Boolean)
      .join(' - ')
    return (
      <div
        role="group"
        aria-label={t.phoneInput.aria.group}
        aria-disabled="true"
        className="flex min-h-[2.5rem] items-center rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-content-tertiary cursor-not-allowed dark:border-dark-border-primary dark:bg-dark-bg-secondary dark:text-dark-content-tertiary"
        id={id}
      >
        {display || '\u2014'}
      </div>
    )
  }

  return (
    <div
      role="group"
      aria-label={t.phoneInput.aria.group}
      className={cn(
        'flex items-center rounded-lg border bg-bg-primary text-sm text-content-primary transition-all duration-150',
        'focus-within:ring-2 focus-within:ring-[#329cff] dark:focus-within:ring-[#329cff]',
        'dark:bg-dark-bg-primary dark:border-dark-border-primary dark:text-dark-content-primary',
        hasError
          ? 'border-red-500 focus-within:ring-red-500'
          : 'border-border-primary',
      )}
      id={id}
      aria-describedby={ariaDescribedBy}
    >
      <input
        ref={areaRef}
        type="text"
        inputMode="numeric"
        value={value.areaCode}
        onChange={handleAreaChange}
        onKeyDown={handleKeyDown('area')}
        placeholder={t.phoneInput.placeholder.area}
        maxLength={AREA_MAX}
        aria-label={t.phoneInput.aria.areaCode}
        className="w-16 bg-transparent py-2.5 pl-3 pr-1 outline-none placeholder:text-content-tertiary/60 dark:placeholder:text-dark-content-tertiary/60"
      />
      <input
        ref={numberRef}
        type="text"
        inputMode="numeric"
        value={value.number}
        onChange={handleNumberChange}
        onKeyDown={handleKeyDown('number')}
        placeholder={t.phoneInput.placeholder.number}
        maxLength={NUMBER_MAX}
        aria-label={t.phoneInput.aria.number}
        className="flex-1 min-w-0 bg-transparent py-2.5 px-1 outline-none placeholder:text-content-tertiary/60 dark:placeholder:text-dark-content-tertiary/60"
      />
      <input
        ref={extensionRef}
        type="text"
        inputMode="numeric"
        value={value.extension}
        onChange={handleExtensionChange}
        onKeyDown={handleKeyDown('extension')}
        placeholder={t.phoneInput.placeholder.extension}
        maxLength={EXTENSION_MAX}
        aria-label={t.phoneInput.aria.extension}
        className="w-14 bg-transparent py-2.5 pl-1 pr-3 outline-none placeholder:text-content-tertiary/60 dark:placeholder:text-dark-content-tertiary/60"
      />
    </div>
  )
}
